import { BadgeCheck } from 'lucide-react';
import type { User, UserStatus } from '@/types/user.types';
import { formatRelativeTime } from '@/utils/formatters';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { ItemLista, Selo, AvatarIniciais } from '@/components/common/ListaMobile';
import { roleLabels, ASSINATURA_LABEL, STATUS_COBRE } from './userDisplay';
import { UserActionsMenu } from './UserActionsMenu';

interface UserCardProps {
  user: User;
  onViewDetails?: (id: string) => void;
  onEdit?: (id: string) => void;
  onBlock?: (id: string) => void;
  onUnblock?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageSubscription?: (id: string) => void;
}

type TomSelo = 'sucesso' | 'erro' | 'aviso' | 'info' | 'neutro' | 'destaque';

const STATUS_SELO: Record<UserStatus, { rotulo: string; tom: TomSelo }> = {
  active: { rotulo: 'Ativo', tom: 'sucesso' },
  inactive: { rotulo: 'Inativo', tom: 'neutro' },
  blocked: { rotulo: 'Bloqueado', tom: 'erro' },
  suspended: { rotulo: 'Suspenso', tom: 'aviso' },
};

// Mesmas cores da pílula de assinatura da tabela, no tom do Selo.
const ASSINATURA_TOM: Record<string, TomSelo> = {
  trial: 'info',
  active: 'sucesso',
  comp: 'destaque',
  past_due: 'aviso',
  canceled: 'erro',
  expired: 'erro',
  none: 'neutro',
};

// Mesma prioridade do SubscriptionBadge, mas só leitura: dentro da linha
// tocável não cabe outro botão. Gerenciar fica no menu de três pontos.
function resumoAssinatura(user: User): { texto: string; tom: TomSelo } | null {
  if (user.isProducer && user.assinatura) {
    const rotulo = ASSINATURA_LABEL[user.assinatura.status] ?? user.assinatura.status;
    const plano = user.assinatura.plano?.nome;
    return {
      texto: plano ? `${rotulo} · ${plano}` : rotulo,
      tom: ASSINATURA_TOM[user.assinatura.status] ?? 'neutro',
    };
  }
  if (user.coberturaHerdada && STATUS_COBRE.has(user.coberturaHerdada.status)) {
    const plano = user.coberturaHerdada.plano?.nome;
    return { texto: plano ? `Coberto · ${plano}` : 'Coberto', tom: 'info' };
  }
  if (user.isProducer) return { texto: 'Sem assinatura', tom: 'neutro' };
  return null;
}

// "Acesso há 2 horas" / "Acesso em 12/10/2026 14:00" / "Nunca acessou".
function textoAcesso(lastAccess: string): string {
  const rel = formatRelativeTime(lastAccess);
  if (rel === 'Nunca') return 'Nunca acessou';
  if (rel.startsWith('Há') || rel === 'Agora mesmo') return `Acesso ${rel.toLowerCase()}`;
  return `Acesso em ${rel}`;
}

// Linha do usuário na lista do celular (padrão ItemLista). A linha abre os
// detalhes; editar, bloquear, assinatura e excluir ficam no menu à direita.
export function UserCard({
  user,
  onViewDetails,
  onEdit,
  onBlock,
  onUnblock,
  onDelete,
  onManageSubscription,
}: UserCardProps) {
  const propName = typeof user.property === 'string' ? user.property : user.property?.name || 'N/A';
  const cargo = roleLabels[user.role] || user.cargo || user.role;
  const status = STATUS_SELO[user.status] ?? { rotulo: user.status, tom: 'neutro' as const };
  const assinatura = resumoAssinatura(user);
  const aparelho = user.lastDevice && user.lastDevice !== 'N/A' ? user.lastDevice : null;

  return (
    <ItemLista
      inicio={
        <AvatarIniciais iniciais={getUserInitials(user.name)} className={getAvatarColor(user.id)} />
      }
      titulo={user.name}
      tituloExtra={
        user.verified && (
          <BadgeCheck size={15} className="shrink-0 text-info" aria-label="Verificado" />
        )
      }
      apoio={`${cargo} · ${propName}`}
      meta={
        <span className="truncate">
          {textoAcesso(user.lastAccess)}
          {aparelho && ` · ${aparelho}`}
        </span>
      }
      // Só o bloqueio ganha selo: um "Ativo" verde ao lado da assinatura parecia
      // plano ativo. A API só devolve active ou blocked; a tabela nem tem coluna.
      fim={
        <>
          {user.status === 'blocked' && <Selo tom={status.tom}>{status.rotulo}</Selo>}
          {assinatura && (
            <Selo tom={assinatura.tom} className="max-w-[7.5rem]">
              <span className="min-w-0 truncate">{assinatura.texto}</span>
            </Selo>
          )}
        </>
      }
      // Sem seta: com o menu de três pontos ao lado ela espremia o nome, e a
      // linha inteira já abre os detalhes.
      onClick={onViewDetails ? () => onViewDetails(user.id) : undefined}
      acao={
        <UserActionsMenu
          user={user}
          onViewDetails={onViewDetails}
          onEdit={onEdit}
          onBlock={onBlock}
          onUnblock={onUnblock}
          onDelete={onDelete}
          onManageSubscription={onManageSubscription}
        />
      }
    />
  );
}
